import React, { useLayoutEffect, useRef, useState } from 'react'

import {
  clampFloatActionsPosition,
  readFloatActionsPosition,
  writeFloatActionsPosition,
} from '#/components/FloatActions/Logic/floatActionsPosition'
import type { FloatActionsDragStart } from '#/components/FloatActions/Logic/types'
import type { FloatActionsPosition } from '#/components/FloatActions/types'

/** Deslocamento mínimo em px para o gesto deixar de ser clique e virar arrasto */
const DRAG_THRESHOLD = 5

/** Posição do grupo de ações, alterada apenas pelo arrasto e persistida no navegador */
export function useFloatActionsDrag() {
  const elementRef = useRef<HTMLDivElement>(null)
  const dragStartRef = useRef<FloatActionsDragStart | null>(null)
  const draggedPositionRef = useRef<FloatActionsPosition | null>(null)
  const wasDraggedRef = useRef(false)

  const [position, setPosition] = useState(readFloatActionsPosition)
  const [isDragging, setIsDragging] = useState(false)

  function clampToViewport(candidate: FloatActionsPosition): FloatActionsPosition {
    const element = elementRef.current

    if (!element) {
      return candidate
    }

    return clampFloatActionsPosition(
      candidate,
      { height: element.offsetHeight, width: element.offsetWidth },
      { height: window.innerHeight, width: window.innerWidth },
    )
  }

  /** Posição gravada em uma janela maior volta para dentro da tela ao carregar */
  useLayoutEffect(() => {
    setPosition(current => current && clampToViewport(current))
  }, [])

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) {
      return
    }

    const { bottom, right } = event.currentTarget.getBoundingClientRect()

    wasDraggedRef.current = false
    dragStartRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      position: { x: right, y: bottom },
    }
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const dragStart = dragStartRef.current

    if (!dragStart) {
      return
    }

    const deltaX = event.clientX - dragStart.pointerX
    const deltaY = event.clientY - dragStart.pointerY

    if (!wasDraggedRef.current) {
      if (Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD) {
        return
      }

      wasDraggedRef.current = true
      event.currentTarget.setPointerCapture(event.pointerId)
      setIsDragging(true)
    }

    const draggedPosition = clampToViewport({
      x: dragStart.position.x + deltaX,
      y: dragStart.position.y + deltaY,
    })

    draggedPositionRef.current = draggedPosition
    setPosition(draggedPosition)
  }

  function handlePointerEnd() {
    dragStartRef.current = null
    setIsDragging(false)

    if (draggedPositionRef.current) {
      writeFloatActionsPosition(draggedPositionRef.current)
      draggedPositionRef.current = null
    }
  }

  /** O clique que encerra um arrasto não chega aos botões; o clique por teclado tem detail igual a zero */
  function handleClickCapture(event: React.MouseEvent<HTMLDivElement>) {
    if (!wasDraggedRef.current || event.detail === 0) {
      return
    }

    wasDraggedRef.current = false
    event.preventDefault()
    event.stopPropagation()
  }

  return {
    elementRef,
    handlers: {
      onClickCapture: handleClickCapture,
      onDragStart: (event: React.DragEvent<HTMLDivElement>) => event.preventDefault(),
      onPointerCancel: handlePointerEnd,
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerEnd,
    },
    isDragging,
    position,
  }
}
